import { useState, useEffect, useCallback } from 'react';
import { Intervention, FilterParams, Stats } from '../types';
import { interventionsApi } from '../services/api';

// ===== Hook utiliserInterventions =====

export const useInterventions = () => {
  const [interventions, setInterventions] = useState<Intervention[]>([]);
  const [allInterventions, setAllInterventions] = useState<Intervention[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [filters, setFilters] = useState<FilterParams>({});
  const [stats, setStats] = useState<Stats>({
    totalRealized: 0,
    totalRefused: 0,
    totalValidated: 0,
    validatedThisWeek: 0,
    validatedNextWeek: 0,
    totalPending: 0,
    totalCancelled: 0,
  });

  const getWeekNumber = useCallback((date: Date): number => {
    const startOfYear = new Date(date.getFullYear(), 0, 1);
    const days = Math.floor((date.getTime() - startOfYear.getTime()) / (24 * 60 * 60 * 1000));
    return Math.ceil((days + startOfYear.getDay() + 1) / 7);
  }, []);

  const calculateStats = useCallback((allInterventions: Intervention[]) => {
    const now = new Date();
    const currentWeekNumber = getWeekNumber(now);
    const nextWeekNumber = currentWeekNumber + 1;

    const isAllEnginsRealized = (intervention: Intervention): boolean => {
      const engins = [intervention.engin_1, intervention.engin_2, intervention.engin_3, intervention.engin_4].filter(Boolean) as string[];
      if (engins.length === 0) return false;

      try {
        const realiseData = intervention.realise_donnees ? JSON.parse(intervention.realise_donnees) : {};
        const validationData = intervention.validation_donnees ? JSON.parse(intervention.validation_donnees) : {};
        const enginIds = engins.map((_, index) => `engin_${index + 1}`);
        
        const nonRefusedEnginIds = enginIds.filter(enginId => 
          validationData[enginId] !== 'not_feasible'
        );
        
        if (nonRefusedEnginIds.length === 0) return false;
        
        return nonRefusedEnginIds.every(enginId => realiseData[enginId] === 'done');
      } catch {
        return false;
      }
    };

    const isAllEnginsRefused = (intervention: Intervention): boolean => {
      const engins = [intervention.engin_1, intervention.engin_2, intervention.engin_3, intervention.engin_4].filter(Boolean) as string[];
      if (engins.length === 0) return false;

      try {
        const validationData = intervention.validation_donnees ? JSON.parse(intervention.validation_donnees) : {};
        const enginIds = engins.map((_, index) => `engin_${index + 1}`);
        return enginIds.every(enginId => validationData[enginId] === 'not_feasible');
      } catch {
        return false;
      }
    };

    const isAllEnginsValidated = (intervention: Intervention): boolean => {
      const engins = [intervention.engin_1, intervention.engin_2, intervention.engin_3, intervention.engin_4].filter(Boolean) as string[];
      if (engins.length === 0) return false;

      try {
        const validationData = intervention.validation_donnees ? JSON.parse(intervention.validation_donnees) : {};
        const enginIds = engins.map((_, index) => `engin_${index + 1}`);
        
        const nonRefusedEnginIds = enginIds.filter(enginId => 
          validationData[enginId] !== 'not_feasible'
        );
        
        if (nonRefusedEnginIds.length === 0) return false;
        
        return nonRefusedEnginIds.every(enginId => validationData[enginId] === 'done');
      } catch {
        return false;
      }
    };

    const isAllEnginsPending = (intervention: Intervention): boolean => {
      if (intervention.annulation_client) return false;
      
      const engins = [intervention.engin_1, intervention.engin_2, intervention.engin_3, intervention.engin_4].filter(Boolean) as string[];
      if (engins.length === 0) return false;

      try {
        const validationData = intervention.validation_donnees ? JSON.parse(intervention.validation_donnees) : {};
        const enginIds = engins.map((_, index) => `engin_${index + 1}`);
        
        const nonRefusedEnginIds = enginIds.filter(enginId => 
          validationData[enginId] !== 'not_feasible'
        );
        
        if (nonRefusedEnginIds.length === 0) return false;
        
        return nonRefusedEnginIds.every(enginId => !validationData[enginId] || validationData[enginId] === 'pending');
      } catch {
        return false;
      }
    };

    const isNextWeekAvailability = (intervention: Intervention): boolean => {
      if (!intervention.disponibilite_a_partir) return false;
      const availabilityWeekNumber = getWeekNumber(new Date(intervention.disponibilite_a_partir));
      return availabilityWeekNumber === nextWeekNumber;
    };

    const activeInterventions = allInterventions.filter(i => 
      !isAllEnginsRealized(i) && !isAllEnginsRefused(i)
    );

    const stats: Stats = {
      totalRealized: allInterventions.filter(i => isAllEnginsRealized(i)).length,
      totalRefused: allInterventions.filter(i => isAllEnginsRefused(i)).length,
      totalValidated: activeInterventions.filter(i => isAllEnginsValidated(i)).length,
      validatedThisWeek: activeInterventions.filter(i => {
        if (!i.disponibilite_a_partir) return false;
        const availabilityWeekNumber = getWeekNumber(new Date(i.disponibilite_a_partir));
        return availabilityWeekNumber === currentWeekNumber;
      }).length,
      validatedNextWeek: activeInterventions.filter(i => isNextWeekAvailability(i)).length,
      totalPending: activeInterventions.filter(i => isAllEnginsPending(i)).length,
      totalCancelled: allInterventions.filter(i => i.annulation_client === 1 || i.annulation_client === true).length,
    };

    setStats(stats);
  }, [getWeekNumber]);

  const loadInterventions = useCallback(async (statusFilter?: string) => {
    setIsLoading(true);
    try {
      let params = { ...filters };
      
      if (statusFilter && !['realized', 'refused', 'validated', 'validated-next-week', 'validated-week', 'pending'].includes(statusFilter)) {
        params.filtre_statut = statusFilter;
      }

      const response = await interventionsApi.getAll(params);

      if (response.interventions) {
        let interventionsWithWeeks = response.interventions.map(intervention => ({
          ...intervention,
          numero_semaine: getWeekNumber(new Date(intervention.disponibilite_a_partir))
        }));

        if (statusFilter === 'realized') {
          interventionsWithWeeks = interventionsWithWeeks.filter(intervention => {
            const engins = [intervention.engin_1, intervention.engin_2, intervention.engin_3, intervention.engin_4].filter(Boolean) as string[];
            if (engins.length === 0) return false;

            try {
              const realiseData = intervention.realise_donnees ? JSON.parse(intervention.realise_donnees) : {};
              const validationData = intervention.validation_donnees ? JSON.parse(intervention.validation_donnees) : {};
              const enginIds = engins.map((_, index) => `engin_${index + 1}`);
              
              const nonRefusedEnginIds = enginIds.filter(enginId => 
                validationData[enginId] !== 'not_feasible'
              );
              
              if (nonRefusedEnginIds.length === 0) return false;
              
              return nonRefusedEnginIds.every(enginId => realiseData[enginId] === 'done');
            } catch {
              return false;
            }
          });
        } else if (statusFilter === 'refused') {
          interventionsWithWeeks = interventionsWithWeeks.filter(intervention => {
            const engins = [intervention.engin_1, intervention.engin_2, intervention.engin_3, intervention.engin_4].filter(Boolean) as string[];
            if (engins.length === 0) return false;

            try {
              const validationData = intervention.validation_donnees ? JSON.parse(intervention.validation_donnees) : {};
              const enginIds = engins.map((_, index) => `engin_${index + 1}`);
              return enginIds.every(enginId => validationData[enginId] === 'not_feasible');
            } catch {
              return false;
            }
          });
        } else if (statusFilter === 'validated') {
          interventionsWithWeeks = interventionsWithWeeks.filter(intervention => {
            const engins = [intervention.engin_1, intervention.engin_2, intervention.engin_3, intervention.engin_4].filter(Boolean) as string[];
            if (engins.length === 0) return false;

            try {
              const validationData = intervention.validation_donnees ? JSON.parse(intervention.validation_donnees) : {};
              const realiseData = intervention.realise_donnees ? JSON.parse(intervention.realise_donnees) : {};
              const enginIds = engins.map((_, index) => `engin_${index + 1}`);
              
              const nonRefusedEnginIds = enginIds.filter(enginId => 
                validationData[enginId] !== 'not_feasible'
              );
              
              if (nonRefusedEnginIds.length === 0) return false;
              
              const allValidated = nonRefusedEnginIds.every(enginId => validationData[enginId] === 'done');
              if (!allValidated) return false;

              const allRealized = nonRefusedEnginIds.every(enginId => realiseData[enginId] === 'done');
              if (allRealized) return false;

              const allRefused = enginIds.every(enginId => validationData[enginId] === 'not_feasible');
              if (allRefused) return false;

              return true;
            } catch {
              return false;
            }
          });
        } else if (statusFilter === 'validated-next-week') {
          const nextWeekNumber = getWeekNumber(new Date()) + 1;
          interventionsWithWeeks = interventionsWithWeeks.filter(intervention => {
            if (!intervention.disponibilite_a_partir) return false;
            const availabilityWeekNumber = getWeekNumber(new Date(intervention.disponibilite_a_partir));
            return availabilityWeekNumber === nextWeekNumber;
          });
        } else if (statusFilter === 'validated-week') {
          const currentWeekNumber = getWeekNumber(new Date());
          interventionsWithWeeks = interventionsWithWeeks.filter(intervention => {
            if (!intervention.disponibilite_a_partir) return false;
            const availabilityWeekNumber = getWeekNumber(new Date(intervention.disponibilite_a_partir));
            return availabilityWeekNumber === currentWeekNumber;
          });
        } else if (statusFilter === 'cancelled') {
          interventionsWithWeeks = interventionsWithWeeks.filter(intervention => intervention.annulation_client);
        } else if (statusFilter === 'pending') {
          interventionsWithWeeks = interventionsWithWeeks.filter(intervention => {
            const engins = [intervention.engin_1, intervention.engin_2, intervention.engin_3, intervention.engin_4].filter(Boolean) as string[];
            if (engins.length === 0) return false;

            try {
              const validationData = intervention.validation_donnees ? JSON.parse(intervention.validation_donnees) : {};
              const enginIds = engins.map((_, index) => `engin_${index + 1}`);
              
              const nonRefusedEnginIds = enginIds.filter(enginId => 
                validationData[enginId] !== 'not_feasible'
              );
              
              if (nonRefusedEnginIds.length === 0) return false;
              
              return nonRefusedEnginIds.every(enginId => !validationData[enginId] || validationData[enginId] === 'pending');
            } catch {
              return false;
            }
          });
        } else if (!statusFilter) {
          const currentWeekNumber = getWeekNumber(new Date());
          const nextWeekNumber = currentWeekNumber + 1;

          interventionsWithWeeks = interventionsWithWeeks.filter(intervention => {
            const engins = [intervention.engin_1, intervention.engin_2, intervention.engin_3, intervention.engin_4].filter(Boolean) as string[];
            if (engins.length === 0) return true;

            const availabilityWeekNumber = intervention.disponibilite_a_partir 
              ? getWeekNumber(new Date(intervention.disponibilite_a_partir)) 
              : 0;
            
            const isCurrentOrNextWeek = availabilityWeekNumber === currentWeekNumber || availabilityWeekNumber === nextWeekNumber;

            try {
              const realiseData = intervention.realise_donnees ? JSON.parse(intervention.realise_donnees) : {};
              const validationData = intervention.validation_donnees ? JSON.parse(intervention.validation_donnees) : {};
              const enginIds = engins.map((_, index) => `engin_${index + 1}`);

              const nonRefusedEnginIds = enginIds.filter(enginId => 
                validationData[enginId] !== 'not_feasible'
              );
              
              const isAllRealized = nonRefusedEnginIds.length > 0 && 
                nonRefusedEnginIds.every(enginId => realiseData[enginId] === 'done');
              
              const isAllRefused = enginIds.every(enginId => validationData[enginId] === 'not_feasible');

              if (isAllRealized || isAllRefused) {
                return isCurrentOrNextWeek;
              }

              return true;
            } catch {
              return true;
            }
          });
        }

        if (filters.numero_semaine) {
          const selectedWeeks = filters.numero_semaine.split(',').filter(Boolean).map(w => parseInt(w.trim()));
          if (selectedWeeks.length > 0) {
            interventionsWithWeeks = interventionsWithWeeks.filter(intervention => {
              return selectedWeeks.includes(intervention.numero_semaine);
            });
          }
        }

        setInterventions(interventionsWithWeeks);
      }

      const allResponse = await interventionsApi.getAll();
      if (allResponse.interventions) {
        setAllInterventions(allResponse.interventions);
        calculateStats(allResponse.interventions);
      }
    } catch (error) {
      console.error('Error loading interventions:', error);
    } finally {
      setIsLoading(false);
    }
  }, [filters, calculateStats, getWeekNumber]);

  const createIntervention = async (intervention: Partial<Intervention>) => {
    const response = await interventionsApi.create(intervention);
    if (response.success) {
      await loadInterventions();
      return response;
    }
    throw new Error(response.error);
  };

  const updateIntervention = async (id: number, intervention: Partial<Intervention>) => {
    const response = await interventionsApi.update(id, intervention);
    if (response.success) {
      await loadInterventions();

      window.dispatchEvent(new CustomEvent('pendingModificationsUpdated'));

      return response;
    }
    throw new Error(response.error);
  };

  const deleteIntervention = async (id: number) => {
    const response = await interventionsApi.delete(id);
    if (response.success) {
      await loadInterventions();
      return response;
    }
    throw new Error(response.error);
  };

  useEffect(() => {
    void loadInterventions();
  }, [loadInterventions]);

  return {
    interventions,
    allInterventions,
    isLoading,
    stats,
    filters,
    setFilters,
    loadInterventions,
    createIntervention,
    updateIntervention,
    deleteIntervention,
  };
};
